import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { ROLES } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { PortalShell } from "@/components/PortalShell";

import Login from "@/pages/Login";
import RoleRedirect from "@/pages/RoleRedirect";

// Comercial
import Comercial from "@/pages/comercial/Comercial";

// Secretaria
import SecretariaDashboard from "@/pages/secretaria/Dashboard";
import SecretariaContracts from "@/pages/secretaria/ContractsList";
import Agenda from "@/pages/secretaria/Agenda";
import NovosClientes from "@/pages/secretaria/NovosClientes";

// Shared (Contract & Document detail used by multiple roles)
import ContractDetail from "@/pages/shared/ContractDetail";
import DocumentDetail from "@/pages/shared/DocumentDetail";
import MeetingDetail from "@/pages/shared/MeetingDetail";
import PrestacaoContas from "@/pages/shared/PrestacaoContas";

// Consultor
import ConsultorDashboard from "@/pages/consultor/Dashboard";
import MyRanking from "@/pages/consultor/MyRanking";

// Juridico
import JuridicoDashboard from "@/pages/juridico/Dashboard";
import Reviews from "@/pages/juridico/Reviews";

// Diretora
import DiretoraDashboard from "@/pages/diretora/Dashboard";
import Approvals from "@/pages/diretora/Approvals";
import Products from "@/pages/diretora/Products";
import Settings from "@/pages/diretora/Settings";
import UsersPage from "@/pages/diretora/Users";

// Shared (Canal, Treinamento, Providencias, Ranking)
import CanalDiretoraSecretaria from "@/pages/shared/CanalDiretoraSecretaria";
import Training from "@/pages/shared/Training";
import Providencias from "@/pages/shared/Providencias";
import Ranking from "@/pages/shared/Ranking";

// Portal Cliente
import PortalDashboard from "@/pages/portal/PortalDashboard";
import PortalContract from "@/pages/portal/PortalContract";

// 404
import NotFound from "@/pages/NotFound";

// Forced password change
import ChangePassword from "@/pages/ChangePassword";

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/trocar-senha" element={<ChangePassword />} />

          {/* Root \u2192 redirect by role */}
          <Route path="/" element={<RoleRedirect />} />

          {/* Internal AppShell routes */}
          <Route
            element={
              <ProtectedRoute
                roles={[
                  ROLES.COMERCIAL,
                  ROLES.SECRETARIA,
                  ROLES.CONSULTOR,
                  ROLES.JURIDICO,
                  ROLES.DIRETORA,
                ]}
              >
                <AppShell />
              </ProtectedRoute>
            }
          >
            {/* Comercial */}
            <Route path="/comercial" element={
              <ProtectedRoute roles={[ROLES.COMERCIAL, ROLES.DIRETORA, ROLES.SECRETARIA]}>
                <Comercial />
              </ProtectedRoute>
            } />
            <Route path="/diretora/novo-cadastro" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <Comercial />
              </ProtectedRoute>
            } />

            {/* Secretaria */}
            <Route path="/secretaria/dashboard" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <SecretariaDashboard />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/contratos" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <SecretariaContracts />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/contratos/:id" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <ContractDetail />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/agenda" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA, ROLES.CONSULTOR]}>
                <Agenda />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/reunioes/:id" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA, ROLES.CONSULTOR, ROLES.JURIDICO]}>
                <MeetingDetail />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/novos-clientes" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <NovosClientes />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/providencias" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <Providencias />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/canal-diretora" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <CanalDiretoraSecretaria />
              </ProtectedRoute>
            } />

            {/* Consultor */}
            <Route path="/consultor/dashboard" element={
              <ProtectedRoute roles={[ROLES.CONSULTOR]}>
                <ConsultorDashboard />
              </ProtectedRoute>
            } />
            <Route path="/consultor/contratos" element={
              <ProtectedRoute roles={[ROLES.CONSULTOR]}>
                <SecretariaContracts />
              </ProtectedRoute>
            } />
            <Route path="/consultor/contratos/:id" element={
              <ProtectedRoute roles={[ROLES.CONSULTOR]}>
                <ContractDetail />
              </ProtectedRoute>
            } />
            <Route path="/consultor/treinamento" element={
              <ProtectedRoute roles={[ROLES.CONSULTOR, ROLES.DIRETORA, ROLES.JURIDICO, ROLES.SECRETARIA]}>
                <Training />
              </ProtectedRoute>
            } />
            <Route path="/consultor/meu-ranking" element={
              <ProtectedRoute roles={[ROLES.CONSULTOR]}>
                <MyRanking />
              </ProtectedRoute>
            } />

            {/* Juridico */}
            <Route path="/juridico/dashboard" element={
              <ProtectedRoute roles={[ROLES.JURIDICO]}>
                <JuridicoDashboard />
              </ProtectedRoute>
            } />
            <Route path="/juridico/revisoes" element={
              <ProtectedRoute roles={[ROLES.JURIDICO]}>
                <Reviews />
              </ProtectedRoute>
            } />
            <Route path="/juridico/contratos" element={
              <ProtectedRoute roles={[ROLES.JURIDICO]}>
                <SecretariaContracts />
              </ProtectedRoute>
            } />
            <Route path="/juridico/contratos/:id" element={
              <ProtectedRoute roles={[ROLES.JURIDICO]}>
                <ContractDetail />
              </ProtectedRoute>
            } />
            <Route path="/juridico/ranking" element={
              <ProtectedRoute roles={[ROLES.JURIDICO, ROLES.DIRETORA]}>
                <Ranking />
              </ProtectedRoute>
            } />

            {/* Diretora */}
            <Route path="/diretora/dashboard" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <DiretoraDashboard />
              </ProtectedRoute>
            } />
            <Route path="/diretora/agenda" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <Agenda basePath="/diretora" />
              </ProtectedRoute>
            } />
            <Route path="/diretora/reunioes/:id" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <MeetingDetail />
              </ProtectedRoute>
            } />
            <Route path="/diretora/aprovacoes" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <Approvals />
              </ProtectedRoute>
            } />
            <Route path="/diretora/contratos" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <SecretariaContracts />
              </ProtectedRoute>
            } />
            <Route path="/diretora/contratos/:id" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <ContractDetail />
              </ProtectedRoute>
            } />
            <Route path="/diretora/produtos" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <Products />
              </ProtectedRoute>
            } />
            <Route path="/diretora/usuarios" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <UsersPage />
              </ProtectedRoute>
            } />
            {/* Melhoria 5: Secretária também pode gerenciar vínculos cliente-contrato */}
            <Route path="/secretaria/usuarios" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <UsersPage />
              </ProtectedRoute>
            } />
            <Route path="/diretora/configuracoes" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <Settings />
              </ProtectedRoute>
            } />
            <Route path="/diretora/canal-secretaria" element={
              <ProtectedRoute roles={[ROLES.DIRETORA, ROLES.SECRETARIA]}>
                <CanalDiretoraSecretaria />
              </ProtectedRoute>
            } />
            <Route path="/diretora/ranking" element={
              <ProtectedRoute roles={[ROLES.DIRETORA, ROLES.JURIDICO]}>
                <Ranking />
              </ProtectedRoute>
            } />
            <Route path="/diretora/treinamentos" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <Training />
              </ProtectedRoute>
            } />

            {/* Universal document detail */}
            <Route path="/documentos/:id" element={
              <ProtectedRoute
                roles={[ROLES.CONSULTOR, ROLES.SECRETARIA, ROLES.JURIDICO, ROLES.DIRETORA]}
              >
                <DocumentDetail />
              </ProtectedRoute>
            } />

            {/* Prestação de Contas — Diretora / Secretária */}
            <Route path="/diretora/contratos/:id/prestacao" element={
              <ProtectedRoute roles={[ROLES.DIRETORA]}>
                <PrestacaoContas />
              </ProtectedRoute>
            } />
            <Route path="/secretaria/contratos/:id/prestacao" element={
              <ProtectedRoute roles={[ROLES.SECRETARIA, ROLES.DIRETORA]}>
                <PrestacaoContas />
              </ProtectedRoute>
            } />
          </Route>

          {/* Portal Cliente (external) */}
          <Route
            element={
              <ProtectedRoute roles={[ROLES.CLIENTE]}>
                <PortalShell />
              </ProtectedRoute>
            }
          >
            <Route path="/portal" element={<PortalDashboard />} />
            <Route path="/portal/contratos/:id" element={<PortalContract />} />
            <Route path="/portal/contratos/:id/prestacao" element={<PrestacaoContas />} />
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
